import {
  ActionIcon,
  Badge,
  Box,
  Button, Card,
  Divider,
  Group,
  Loader,
  Modal,
  ModalProps,
  ScrollArea,
  Stack,
  Text,
  TextInput
} from "@mantine/core";
import {
  EntityObject,
  getObjectId,
  getObjectType,
  matchesTagDefinition,
  normalizeTagDefinitionKey,
  normalizeTagInput,
  tagAssignmentsSelector,
  TagDefinitionEntity,
  tagDefinitionSourceKind,
  tagDefinitionsSelectorWithParams,
  useGetAllTagAssignmentsByObjectQuery,
  useGetAllTagDefinitionsQuery
} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {IconPlus, IconSearch, IconX} from "@tabler/icons-react";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {useTagDefinitionMutation} from "../../../hooks/useTagDefinitionMutation";
import {useTagAssignmentMutation} from "../../../hooks/useTagAssignmentMutation";

export type ObjectTagManagerModal = Omit<ModalProps, 'title'> & {
  entity: EntityObject
}

export const ObjectTagManagerModal = (props: ObjectTagManagerModal) => {
  const {entity, opened, onClose, ...rest} = props;

  const {t} = useTranslation(["common"]);

  const [query, setQuery] = useState("");
  const [initialAssignedTagIds, setInitialAssignedTagIds] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const didInitAssignedTagsRef = useRef(false);

  const {
    createTagDefinition,
    isCreating: isCreatingTagDefinition,
  } = useTagDefinitionMutation()

  const {
    replaceTagAssignments,
    isUpdating: isSaving,
  } = useTagAssignmentMutation()

  const selectTagDefinitions = useMemo(() => {
    return tagDefinitionsSelectorWithParams();
  }, []);

  const {tagDefinitions, isLoadingTagDefinitions} = useGetAllTagDefinitionsQuery(undefined, {
    selectFromResult: (result) => ({
      tagDefinitions: selectTagDefinitions(result, {doNotUse: false}),
      isLoadingTagDefinitions: result.isLoading
    })
  });

  const selectTagAssignments = useMemo(() => {
    return tagAssignmentsSelector();
  }, []);

  const {assignedTags, isLoadingAssignedTags, assignedTagsInit} = useGetAllTagAssignmentsByObjectQuery({
    id: getObjectId(entity),
    type: getObjectType(entity),
  }, {
    selectFromResult: (result) => ({
      assignedTags: selectTagAssignments(result),
      isLoadingAssignedTags: result.isLoading,
      assignedTagsInit: !result.isUninitialized
    })
  });

  const assignedTagIds = useMemo(() => {
    return (assignedTags ?? []).map((tag) => tag.tagDefinitionId);
  }, [assignedTags]);

  useEffect(() => {
    if (!opened) {
      didInitAssignedTagsRef.current = false;
      return;
    }

    if (isLoadingAssignedTags || !assignedTagsInit) {
      return;
    }

    if (didInitAssignedTagsRef.current) {
      return;
    }

    didInitAssignedTagsRef.current = true;

    setInitialAssignedTagIds(assignedTagIds);

    setSelectedTagIds((prev) => {
      if (prev.length > 0) {
        return prev;
      }
      const merged = new Set([...prev, ...assignedTagIds]);
      return [...merged];
    });

    setQuery("");
  }, [opened, isLoadingAssignedTags, assignedTagsInit, assignedTagIds]);

  const selectedIdSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);

  const eligibleTagDefinitions = useMemo(() => {
    return [...tagDefinitions]
      .filter((def) => !selectedIdSet.has(def.id))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [tagDefinitions, selectedIdSet]);

  const filteredTagDefinitions = useMemo(() => {
    return [...eligibleTagDefinitions]
      .filter((def) => matchesTagDefinition(def, query))
      .sort((a, b) => a.label.localeCompare(b.label))
      .slice(0, Math.max(0, Math.min(5, eligibleTagDefinitions.length)));
  }, [query, eligibleTagDefinitions]);

  const selectedTags = useMemo(() => {
    const map = new Map(tagDefinitions.map((t) => [t.id, t]));
    return selectedTagIds
      .map((id) => map.get(id))
      .filter((t): t is TagDefinitionEntity => !!t)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedTagIds, tagDefinitions]);

  const dirty = useMemo(() => {
    const original = [...assignedTags.map((t) => t.id)].sort();
    const next = [...selectedTagIds].sort();
    return JSON.stringify(original) !== JSON.stringify(next);
  }, [assignedTags, selectedTagIds]);

  const exactMatch = useMemo(() => {
    const normalizedInput = normalizeTagInput(query);
    const normalizedKey = normalizeTagDefinitionKey(query);

    if (!normalizedInput) {
      return undefined;
    }

    return tagDefinitions.find((tag) => {
      return (
        normalizeTagInput(tag.label) === normalizedInput ||
        normalizeTagDefinitionKey(tag.key) === normalizedKey
      );
    });
  }, [tagDefinitions, query]);

  const canCreate = query.trim().length > 0 && !exactMatch;

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }
      return [...prev, tagId];
    });
  };

  const removeTag = (tagId: string) => {
    setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
  };

  const _onClose = useCallback(() => {
    onClose?.()
  }, [onClose])

  const handleCreateTagDefinition = useCallback(() => {
    if (!query.trim()) {
      return
    }

    createTagDefinition({
      label: query.trim(),
      sourceKind: tagDefinitionSourceKind.user.code,
    }, {
      onSuccess: (def) => {
        setSelectedTagIds((prev) => [...prev, def.id])
        setQuery("")
      }
    })

  }, [createTagDefinition, query])

  const handleSave = useCallback(() => {
    replaceTagAssignments(
      selectedTagIds,
      entity,
      {
        onSuccess: () => {
          _onClose()
        }
      })
  }, [replaceTagAssignments, selectedTagIds, _onClose, entity])

  const isLoading = useMemo(() => isLoadingTagDefinitions || isLoadingAssignedTags, [isLoadingTagDefinitions, isLoadingAssignedTags]);

  return (
    <Modal
      {...rest}
      opened={opened}
      onClose={_onClose}
      title={
        <Stack spacing={0}>
          <Text fw={600}>
            {t("common.tagassignment.modal.manager.title", "Manage Tags")}
          </Text>
          <Text size="sm" c="dimmed">
            <EntityAnchor value={entity} icon={true}/>
          </Text>
        </Stack>
      }
      size="lg"
      centered
    >
      <Stack spacing="md">
        {isLoading ? (
          <Group spacing="center" py="xl">
            <Loader size="sm"/>
          </Group>
        ) : (
          <>
            <Card shadow="xs" padding="lg" radius="md" withBorder={true}>
              <Text size="sm" fw={500} mb="xs">
                {t("common.tagassignment.modal.manager.assignedtags", "Assigned Tags")}
              </Text>

              {selectedTags.length === 0 ? (
                <Text size="sm" c="dimmed">
                  {t("common.tagassignment.modal.manager.notagsassigned", "No tags assigned")}
                </Text>
              ) : (
                <Group spacing="xs">
                  {selectedTags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant="light"
                      rightSection={
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          onClick={() => removeTag(tag.id)}
                        >
                          <IconX size={10}/>
                        </ActionIcon>
                      }
                    >
                      {tag.label}
                    </Badge>
                  ))}
                </Group>
              )}
            </Card>

            <Divider variant="dotted" />

            <TextInput
              size="xs"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={t("common.tagassignment.modal.manager.searchplaceholder", "Search or create tag...")}
              rightSection={<IconSearch size={16}/>}
              onKeyDown={async (event) => {
                if (event.key === "Enter" && canCreate) {
                  event.preventDefault();
                  handleCreateTagDefinition();
                }
              }}
            />

            {canCreate && (
              <Button
                size="xs"
                variant="light"
                leftIcon={<IconPlus size={16}/>}
                onClick={handleCreateTagDefinition}
                loading={isCreatingTagDefinition}
              >
                {t("common.tagassignment.modal.manager.createbutton", "Create tag")}
                {" "}
                “{query.trim()}”
              </Button>
            )}

            <Box>
              <Group spacing="xs">
                <Text size="sm" fw={500} mb="xs">
                  {t("common.tagassignment.modal.manager.availabletags", "Available Tags")}
                </Text>
                <Text size="xs" color="dimmed" mb="xs">
                  {filteredTagDefinitions.length} of {eligibleTagDefinitions.length}
                </Text>
              </Group>

              <ScrollArea mah={320}>
                <Stack spacing="xs">
                  {filteredTagDefinitions.length === 0 && !canCreate ? (
                    <Text size="sm" c="dimmed">
                      {t("common.tagassignment.modal.manager.notagsfound", "No matching tags found")}
                    </Text>
                  ) : (
                    filteredTagDefinitions.map((tag, idx) => {
                      const selected = selectedIdSet.has(tag.id);

                      return (
                        <>
                        <Group
                          key={tag.id}
                          spacing="space-between"
                          noWrap={true}
                          p="xs"
                          style={{
                            border: "1px solid var(--mantine-color-gray-3)",
                            borderRadius: 8,
                            cursor: "pointer",
                            background: selected
                              ? "var(--mantine-color-blue-0)"
                              : undefined,
                          }}
                          onClick={() => toggleTag(tag.id)}
                        >
                          <Box style={{flex: 1}}>
                            <Group spacing="xs">
                              <Badge size="sm" variant={selected ? "filled" : "light"}>
                                {tag.label}
                              </Badge>
                              <Text size="xs" c="dimmed">
                                #{tag.key}
                              </Text>
                            </Group>
                          </Box>

                          <Button
                            size="xs"
                            variant={selected ? "light" : "filled"}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTag(tag.id);
                            }}
                          >
                            {selected ? "Remove" : "Add"}
                          </Button>
                        </Group>
                          {idx !== filteredTagDefinitions.length - 1 && <Divider variant="dashed" />}
                        </>
                      );
                    })
                  )}
                </Stack>
              </ScrollArea>
            </Box>

            <Divider />

            <Group position="apart" mt="sm">
              <Text size="xs" c="dimmed">
                {selectedTagIds.length} selected
              </Text>

              <Group>
                <Button size="xs" variant="default" onClick={_onClose}>
                  {t("common.action.cancel", "Cancel")}
                </Button>
                <Button size="xs" onClick={handleSave} loading={isSaving} disabled={!dirty}>
                  {t("common.action.save", "Save")}
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
};
